import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet, Font } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';
import { amountInWords } from './amountInWords';

const ORANGE = '#F97316';
const INK    = '#0F172A';
const BODY   = '#475569';
const MUTED  = '#94A3B8';
const LINE   = '#E2E8F0';
const SOFT   = '#F8FAFC';
const WHITE  = '#FFFFFF';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function fmtDate(d) {
  if (!d) return '';
  const p = d.split('-');
  if (p.length !== 3) return d;
  return `${parseInt(p[2])} ${MONTHS[parseInt(p[1]) - 1]} ${p[0]}`;
}
function fmtMoney(n) {
  const v = Number(n || 0);
  return 'Rs. ' + v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function fmtTerms(term) {
  const map = { net30: 'Net 30', net45: 'Net 45', net60: 'Net 60', net90: 'Net 90' };
  return map[term] || 'Custom';
}

const s = StyleSheet.create({
  page:        { backgroundColor: WHITE, paddingTop: 50, paddingHorizontal: 45, paddingBottom: 40, fontFamily: 'Helvetica', fontSize: 10, color: INK },
  accentStrip: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: ORANGE },

  // Header
  header:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  titleBlock:  { flexDirection: 'column' },
  title:       { fontSize: 22, fontFamily: 'Helvetica-Bold', color: INK, letterSpacing: -0.5 },
  poweredBy:   { fontSize: 8, color: MUTED, marginTop: 4, letterSpacing: 0.2 },
  invNum:      { fontSize: 10, color: MUTED, marginTop: 4 },
  bizBlock:    { alignItems: 'flex-end' },
  logoBox:     { width: 42, height: 42, backgroundColor: ORANGE, borderRadius: 6, alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  logoLetter:  { color: WHITE, fontSize: 24, fontFamily: 'Helvetica-Bold' },
  bizName:     { fontSize: 12, fontFamily: 'Helvetica-Bold', color: INK, textAlign: 'right' },
  bizDetail:   { fontSize: 9, color: BODY, textAlign: 'right', marginTop: 2, lineHeight: 1.5 },

  // Divider
  hr:          { height: 0.75, backgroundColor: LINE, marginVertical: 14 },
  hrDark:      { height: 1, backgroundColor: INK, marginVertical: 0 },

  // Dates
  datesRow:    { flexDirection: 'row', marginBottom: 14 },
  dateCol:     { marginRight: 32 },
  dateLabel:   { fontSize: 8, fontFamily: 'Helvetica-Bold', color: ORANGE, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 },
  dateVal:     { fontSize: 13, fontFamily: 'Helvetica-Bold', color: INK },

  // Bill To
  sectionLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: ORANGE, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 },
  customerName: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: INK, marginBottom: 3 },
  customerSub:  { fontSize: 9, color: BODY, lineHeight: 1.5 },

  // Table
  tableHeader:  { flexDirection: 'row', paddingBottom: 6, marginTop: 16 },
  tableHeaderCell: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 },
  tableRow:     { flexDirection: 'row', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: LINE },
  tableCell:    { fontSize: 10, color: INK },
  tableCellMuted: { fontSize: 9, color: BODY },
  tableCellBold:  { fontSize: 10, fontFamily: 'Helvetica-Bold', color: INK },

  // Cols widths (% of content width)
  colDesc:     { flex: 3.5 },
  colHsn:      { flex: 1.4 },
  colQty:      { flex: 0.9, textAlign: 'right' },
  colRate:     { flex: 1.6, textAlign: 'right' },
  colAmount:   { flex: 1.8, textAlign: 'right' },

  // Totals
  totalsWrap:  { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 4 },
  totalsInner: { width: 220 },
  totRow:      { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 },
  totLabel:    { fontSize: 11, color: BODY },
  totVal:      { fontSize: 11, fontFamily: 'Helvetica-Bold', color: INK },
  grandLabel:  { fontSize: 14, fontFamily: 'Helvetica-Bold', color: INK },
  grandVal:    { fontSize: 14, fontFamily: 'Helvetica-Bold', color: ORANGE },

  // Words box
  wordsBox:    { backgroundColor: SOFT, borderWidth: 0.5, borderColor: LINE, borderRadius: 4, padding: 10, marginTop: 14, marginBottom: 14 },
  wordsLabel:  { fontSize: 8, fontFamily: 'Helvetica-Bold', color: ORANGE, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 },
  wordsText:   { fontSize: 11, fontFamily: 'Helvetica-Oblique', color: INK },

  // Bottom 2-col
  bottomRow:   { flexDirection: 'row', marginTop: 4 },
  bottomCol:   { flex: 1 },
  signatureCol: { flex: 1, alignItems: 'flex-end' },
  bottomLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: MUTED, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 },
  bottomText:  { fontSize: 10, color: BODY, lineHeight: 1.6 },
  signatureImage: { maxWidth: 150, maxHeight: 70, objectFit: 'contain' },
  signatureName: { fontSize: 9, color: BODY, marginTop: 4, textAlign: 'right' },

});

function HR({ dark } = {}) {
  return <View style={dark ? s.hrDark : s.hr} />;
}

export function InvoicePDF({ invoice, watermark = false }) {
  const { business = {}, customer = {}, lineItems = [], taxSummary = {}, number, date, dueDate, notes, paymentTerms, customPaymentTerms } = invoice;
  const bd = business.bankDetails || {};

  const effRate = taxSummary.taxableAmount > 0
    ? Math.round((((taxSummary.igst || 0) + (taxSummary.cgst || 0) + (taxSummary.sgst || 0)) / taxSummary.taxableAmount) * 100)
    : 18;

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Orange stripe */}
        <View style={s.accentStrip} />

        {/* Header */}
        <View style={s.header}>
          <View style={s.titleBlock}>
            <Text style={s.title}>TAX INVOICE</Text>
            <Text style={s.poweredBy}>powered by billjaldi.com</Text>
            <Text style={s.invNum}># {number || 'INV-001'}</Text>
          </View>
          <View style={s.bizBlock}>
            {business.logo ? (
              <Image src={business.logo} style={{ width: 42, height: 42, marginBottom: 6, objectFit: 'contain' }} />
            ) : (
              <View style={s.logoBox}>
                <Text style={s.logoLetter}>b</Text>
              </View>
            )}
            <Text style={s.bizName}>{business.name || 'Your Business'}</Text>
            {business.address ? <Text style={s.bizDetail}>{business.address}</Text> : null}
            {business.gstin ? <Text style={s.bizDetail}>GSTIN: {business.gstin}</Text> : null}
          </View>
        </View>

        <HR />

        {/* Dates */}
        <View style={s.datesRow}>
          <View style={s.dateCol}>
            <Text style={s.dateLabel}>Date</Text>
            <Text style={s.dateVal}>{fmtDate(date) || '—'}</Text>
          </View>
          {dueDate ? (
            <View style={s.dateCol}>
              <Text style={s.dateLabel}>Due</Text>
              <Text style={s.dateVal}>{fmtDate(dueDate)}</Text>
            </View>
          ) : null}
          {paymentTerms && (paymentTerms !== 'custom' || customPaymentTerms) ? (
            <View style={s.dateCol}>
              <Text style={s.dateLabel}>Terms</Text>
              <Text style={s.dateVal}>{paymentTerms === 'custom' ? customPaymentTerms : fmtTerms(paymentTerms)}</Text>
            </View>
          ) : null}
        </View>

        <HR />

        {/* Bill To */}
        <View style={{ marginBottom: 16 }}>
          <Text style={s.sectionLabel}>Bill To</Text>
          <Text style={s.customerName}>{customer.name || '—'}</Text>
          {customer.gstin ? <Text style={s.customerSub}>GSTIN: {customer.gstin}</Text> : null}
          {customer.phone ? <Text style={s.customerSub}>Phone: {customer.phone}</Text> : null}
          {customer.address ? <Text style={s.customerSub}>{customer.address}</Text> : null}
          {customer.stateName ? <Text style={s.customerSub}>{customer.stateName}{customer.email ? '  ·  ' + customer.email : ''}</Text> : null}
        </View>

        <HR />

        {/* Line items table */}
        <View style={s.tableHeader}>
          <Text style={[s.tableHeaderCell, s.colDesc]}>Description</Text>
          <Text style={[s.tableHeaderCell, s.colHsn]}>HSN</Text>
          <Text style={[s.tableHeaderCell, s.colQty]}>Qty</Text>
          <Text style={[s.tableHeaderCell, s.colRate]}>Rate</Text>
          <Text style={[s.tableHeaderCell, s.colAmount]}>Amount</Text>
        </View>
        <HR dark />

        {lineItems.map((li, i) => {
          const qty   = Number(li.qty || 0);
          const rate  = Number(li.rate || 0);
          const disc  = Number(li.discount || 0);
          const gstR  = Number(li.gstRate || 0);
          const taxable = qty * rate * (1 - disc / 100);
          const total   = taxable * (1 + gstR / 100);
          return (
            <View key={li.id || i} style={s.tableRow}>
              <Text style={[s.tableCell, s.colDesc]}>{li.description || ''}</Text>
              <Text style={[s.tableCellMuted, s.colHsn]}>{li.hsn || ''}</Text>
              <Text style={[s.tableCellMuted, s.colQty]}>{qty % 1 === 0 ? qty : qty.toFixed(2)}</Text>
              <Text style={[s.tableCellMuted, s.colRate]}>{fmtMoney(rate)}</Text>
              <Text style={[s.tableCellBold, s.colAmount]}>{fmtMoney(total)}</Text>
            </View>
          );
        })}

        {/* Totals */}
        <View style={s.totalsWrap}>
          <View style={s.totalsInner}>
            <View style={s.totRow}>
              <Text style={s.totLabel}>Subtotal</Text>
              <Text style={s.totVal}>{fmtMoney(taxSummary.subtotal)}</Text>
            </View>
            {(taxSummary.totalDiscount || 0) > 0 && (
              <View style={s.totRow}>
                <Text style={s.totLabel}>Discount</Text>
                <Text style={s.totVal}>-{fmtMoney(taxSummary.totalDiscount)}</Text>
              </View>
            )}
            {taxSummary.isInterState ? (
              <View style={s.totRow}>
                <Text style={s.totLabel}>IGST @ {effRate}%</Text>
                <Text style={s.totVal}>{fmtMoney(taxSummary.igst)}</Text>
              </View>
            ) : (
              <>
                <View style={s.totRow}>
                  <Text style={s.totLabel}>CGST @ {effRate / 2}%</Text>
                  <Text style={s.totVal}>{fmtMoney(taxSummary.cgst)}</Text>
                </View>
                <View style={s.totRow}>
                  <Text style={s.totLabel}>SGST @ {effRate / 2}%</Text>
                  <Text style={s.totVal}>{fmtMoney(taxSummary.sgst)}</Text>
                </View>
              </>
            )}
            <HR dark />
            <View style={[s.totRow, { marginTop: 6 }]}>
              <Text style={s.grandLabel}>TOTAL</Text>
              <Text style={s.grandVal}>{fmtMoney(taxSummary.grandTotal)}</Text>
            </View>
          </View>
        </View>

        {/* Amount in words */}
        <View style={s.wordsBox}>
          <Text style={s.wordsLabel}>Amount in Words</Text>
          <Text style={s.wordsText}>{amountInWords(taxSummary.grandTotal || 0)}</Text>
        </View>

        <HR />

        {/* Notes + Bank + Signature */}
        <View style={s.bottomRow}>
          {notes ? (
            <View style={s.bottomCol}>
              <Text style={s.bottomLabel}>Notes</Text>
              <Text style={s.bottomText}>{notes}</Text>
            </View>
          ) : null}
          {(bd.bankName || bd.accountNo || bd.ifsc || bd.upi) ? (
            <View style={s.bottomCol}>
              <Text style={s.bottomLabel}>Bank Details</Text>
              {bd.bankName  && <Text style={s.bottomText}>{bd.bankName}</Text>}
              {bd.accountNo && <Text style={s.bottomText}>Acc: {bd.accountNo}</Text>}
              {bd.ifsc      && <Text style={s.bottomText}>IFSC: {bd.ifsc}</Text>}
              {bd.upi       && <Text style={s.bottomText}>UPI: {bd.upi}</Text>}
            </View>
          ) : null}
          {business.sealSignature ? (
            <View style={s.signatureCol}>
              <Text style={s.bottomLabel}>Authorised Signature</Text>
              <Image src={business.sealSignature} style={s.signatureImage} />
              <Text style={s.signatureName}>{business.name || 'Your Business'}</Text>
            </View>
          ) : null}
        </View>
      </Page>
    </Document>
  );
}

export async function generateInvoicePDF(invoice, options = {}) {
  const watermark = typeof options === 'string' ? false : !!options.watermark;
  const blob = await pdf(<InvoicePDF invoice={invoice} watermark={watermark} />).toBlob();
  const ab = await blob.arrayBuffer();
  return new Uint8Array(ab);
}

export function downloadPDF(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function bytesToDataUrl(bytes) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}
